import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { authzBackendEnabled, exchangeClerkSession } from './authorization';
import {
  fetchMe,
  isUnauthenticated,
  localLogin,
  logoutSession,
  refreshSession,
} from './tmrSession';
import type { LocalLoginCredentials, TmrSession } from './tmrSession';

/**
 * Where the TMR session stands. `loading` covers both the boot check and a sign-in in flight, so no
 * screen renders an access decision before the answer is in.
 */
export type TmrSessionStatus = 'loading' | 'active' | 'anonymous';

export interface UseTmrSessionResult {
  status: TmrSessionStatus;
  session: TmrSession | null;
  /** Set when the last sign-in attempt failed for a reason worth showing (never a plain boot 401). */
  error: Error | null;
  /**
   * Whether an identity is still on hand to rebuild the session from — that is, Clerk is signed in.
   *
   * A portal must not offer the password form while this is true with no session: Clerk refuses to sign
   * in a client that already holds a session (`identifier_already_signed_in`), so the form cannot work.
   * {@link recover} is the way out, and signing out of Clerk is the other.
   */
  canRecover: boolean;
  /** Rebuilds the session from the cookie, then from Clerk. Resolves true when one is active. */
  recover: () => Promise<boolean>;
  /** Signs in with the break-glass credentials; resolves once the session is active, throws otherwise. */
  signInLocally: (credentials: LocalLoginCredentials) => Promise<void>;
  /** Revokes the TMR session and signs out of Clerk. Safe to call with no session. */
  signOut: () => Promise<void>;
}

/**
 * How long before expiry the session is rotated. Wide enough that a slow request or a laptop waking
 * from sleep still lands inside the window, short enough not to rotate constantly.
 */
const REFRESH_MARGIN_MS = 60_000;

/** Never schedule a refresh sooner than this — a near-expired session would otherwise spin. */
const MIN_REFRESH_DELAY_MS = 5_000;

/** What one attempt to establish a session concluded. */
export type SessionOutcome =
  | { status: 'active'; session: TmrSession }
  | { status: 'anonymous'; error: Error | null };

/** The three things establishing a session needs, named so the decision can be exercised without React. */
export interface EstablishSessionDeps {
  /** Reads whoever the session cookie belongs to. Rejects with a 401 when there is no session. */
  readSession: () => Promise<TmrSession>;
  /** Trades the identity provider's current token for a fresh session cookie. */
  exchangeIdentity: () => Promise<unknown>;
  /** Whether an identity is on hand at all — false for a signed-out browser. */
  hasIdentity: () => boolean;
}

/**
 * Decides where a session stands: the cookie first, the identity provider second.
 *
 * Lives outside the hook because it is the whole of the fix for the expired session that would not sign
 * back in, and a rule that important should be exercisable without a DOM. The order is the same one the
 * portal has always used on boot — what changed is that a live session that dies now replays it instead
 * of reporting `anonymous` and drawing a login form the provider is bound to refuse.
 *
 * A failure to *read* the cookie that is not a 401 is a real fault and travels with the anonymous
 * outcome; a 401 is simply "no session yet" and is never reported as an error.
 */
export async function establishSession(deps: EstablishSessionDeps): Promise<SessionOutcome> {
  let fault: Error | null = null;
  try {
    return { status: 'active', session: await deps.readSession() };
  } catch (cause) {
    if (!isUnauthenticated(cause)) fault = toError(cause);
  }

  // Nobody left to ask. The login form is the honest next screen — and it only works if a stale failure
  // does not outlive the attempt that produced it, which is why `fault` may well be null here.
  if (!deps.hasIdentity()) return { status: 'anonymous', error: fault };

  try {
    await deps.exchangeIdentity();
    return { status: 'active', session: await deps.readSession() };
  } catch (cause) {
    return { status: 'anonymous', error: toError(cause) };
  }
}

/**
 * Owns the TMR session lifecycle for a portal (skill `frontend-architecture`, Regra 4): recovering it
 * on load, keeping it alive, and ending it.
 *
 * The order matters. It asks the **cookie** first (`GET /auth/me`) and only falls back to exchanging a
 * Clerk token when there is no session — which is what makes the local break-glass login survive a
 * page reload, since that path has no IdP to re-ask. Before slice F1 the portal asked Clerk first, so
 * a local session was forgotten by the next refresh even though the cookie was still valid.
 *
 * That same order is what {@link UseTmrSessionResult.recover} replays whenever a live session dies —
 * a failed rotation, or a 401 from any screen. Until it did, the portal simply reported `anonymous` and
 * drew a login form that Clerk was bound to refuse, because Clerk was still signed in and will not sign
 * in a client that already holds a session. The only way out was reloading the page, which is what made
 * the bug look like it was about the URL.
 */
export function useTmrSession(): UseTmrSessionResult {
  const enabled = authzBackendEnabled();
  const { isLoaded: clerkLoaded, isSignedIn, getToken } = useAuth();
  const { signOut: clerkSignOut } = useClerk();

  // Kept in refs so their changing identity never re-runs the bootstrap effect, and so an attempt
  // started outside it (a rotation failure, a 401) still reads the current values.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const isSignedInRef = useRef(isSignedIn);
  isSignedInRef.current = isSignedIn;

  const [status, setStatus] = useState<TmrSessionStatus>(enabled ? 'loading' : 'anonymous');
  const [session, setSession] = useState<TmrSession | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Every attempt takes a ticket and only the newest one may write state. This replaces the local
  // `cancelled` flag the bootstrap effect used to own, now that an attempt can also start from a timer
  // or from a dead request — two of them must never interleave their results.
  const attemptRef = useRef(0);

  /** Retires whatever attempt is in flight, so its answer can no longer be written. */
  const discardAttempt = useCallback(() => {
    attemptRef.current++;
  }, []);

  const recover = useCallback(async (): Promise<boolean> => {
    const attempt = ++attemptRef.current;

    // An active session stays active while it is being rebuilt. The operator whose cookie just lapsed
    // mid-click should see the click retried, not the whole portal replaced by a spinner and then put
    // back; only a session that is already gone has nothing to keep on screen.
    setStatus((previous) => (previous === 'active' ? previous : 'loading'));

    const outcome = await establishSession({
      readSession: fetchMe,
      exchangeIdentity: () => exchangeClerkSession(() => getTokenRef.current()),
      hasIdentity: () => isSignedInRef.current === true,
    });

    // A newer attempt started while this one was in flight; its answer is the current one.
    if (attemptRef.current !== attempt) return outcome.status === 'active';

    if (outcome.status === 'active') {
      setSession(outcome.session);
      setStatus('active');
      setError(null);
      return true;
    }

    setSession(null);
    setStatus('anonymous');
    setError(outcome.error);
    return false;
  }, []);

  // Recover the session on load, and again whenever Clerk finishes signing someone in.
  useEffect(() => {
    if (!enabled || !clerkLoaded) return;

    void recover();

    // Whatever is in flight belongs to the state we are leaving; the next run takes a fresh ticket.
    return discardAttempt;
  }, [enabled, clerkLoaded, isSignedIn, recover, discardAttempt]);

  // Rotate the session shortly before it expires, so expiry is never discovered by a failed request.
  useEffect(() => {
    if (status !== 'active' || !session) return;

    const msUntilRefresh = new Date(session.expiresAt).getTime() - Date.now() - REFRESH_MARGIN_MS;
    if (Number.isNaN(msUntilRefresh)) return;

    const timer = setTimeout(
      () => {
        void (async () => {
          try {
            setSession(await refreshSession());
          } catch {
            // The cookie is gone — revoked, expired, or the laptop slept straight through the window.
            // Clerk usually still holds the identity, so rebuild from it instead of reporting anonymous:
            // that report was honest about the cookie and useless to the operator, who was then shown a
            // login form Clerk refuses to serve.
            await recover();
          }
        })();
      },
      Math.max(msUntilRefresh, MIN_REFRESH_DELAY_MS),
    );

    return () => clearTimeout(timer);
  }, [status, session, recover]);

  const signInLocally = useCallback(async (credentials: LocalLoginCredentials) => {
    setError(null);
    try {
      const issued = await localLogin(credentials);
      setSession(issued);
      setStatus('active');
    } catch (cause) {
      const failure = toError(cause);
      setError(failure);
      throw failure;
    }
  }, []);

  const signOut = useCallback(async () => {
    // Nothing in flight may resurrect the session the operator just ended.
    discardAttempt();

    // Revoke first: the cookie has to stop being accepted even if signing out of Clerk then fails,
    // and a failure here must not leave the operator stuck on a screen they asked to leave.
    try {
      await logoutSession();
    } catch {
      // Already gone, or the server is unreachable — either way, carry on clearing this end.
    }
    setSession(null);
    setStatus('anonymous');
    setError(null);
    await clerkSignOut();
  }, [clerkSignOut, discardAttempt]);

  return {
    status,
    session,
    error,
    canRecover: isSignedIn ?? false,
    recover,
    signInLocally,
    signOut,
  };
}

/** Normalizes anything thrown into an Error, so callers never branch on the shape of a rejection. */
function toError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error(String(cause));
}
